import React, { type ReactElement, useMemo } from 'react'
import { css, type SerializedStyles } from '@emotion/react'
import { HeaderTitleText } from '@instacart/ids-tooling'
import { ChevronDownIcon, ChevronUpIcon, type Color } from '@instacart/ids-core'
import {
  gridHeaderCellStyles,
  gridSortableCellStyles,
  gridSortedCellStyles,
  gridSortedHeaderCellStyles,
} from '../utils/grid.styles'
import { type GridDatum, type GridColumnInstance } from '../utils/grid.types'
import { spacing } from '../../../foundation'

interface Props<TData extends GridDatum> extends GridColumnInstance<TData> {}

const sortIcon = ({ isSorted, isSortedDesc }: { isSorted: boolean; isSortedDesc: boolean }) => {
  let Chevron = ChevronDownIcon
  let iconColor = 'systemGrayscale30'

  if (isSorted && isSortedDesc) {
    Chevron = ChevronDownIcon
    iconColor = 'systemGrayscale70'
  }

  if (isSorted && !isSortedDesc) {
    Chevron = ChevronUpIcon
    iconColor = 'systemGrayscale70'
  }

  return <Chevron css={{ marginLeft: spacing.X12 }} size={14} color={iconColor as Color} />
}

export function GridHeaderColumnCell<TData extends GridDatum>({
  getHeaderProps,
  getSortByToggleProps,
  render,
  isSorted,
  isSortedDesc,
  canSort,
  headerStyles,
}: Props<TData> & { headerStyles?: SerializedStyles }): ReactElement {
  const renderedHeader = useMemo(() => render('Header'), [render]) as unknown as JSX.Element
  const { style, ...headerProps } = getHeaderProps(getSortByToggleProps())

  const dynamicStyles = css(
    style as SerializedStyles,
    gridHeaderCellStyles,
    canSort && gridSortableCellStyles,
    isSorted && gridSortedCellStyles,
    isSorted && gridSortedHeaderCellStyles,
    headerStyles && headerStyles
  )

  const icon = sortIcon({
    isSorted,
    isSortedDesc: Boolean(isSortedDesc),
  })

  return (
    <div data-testid="header-title" {...headerProps} css={dynamicStyles}>
      <HeaderTitleText>{renderedHeader}</HeaderTitleText>

      {canSort && icon}
    </div>
  )
}
